import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import { seasonService } from '../../services/seasonService';
import { teamService } from '../../services/teamService';
import { userService } from '../../services/userService';
import { useAuth } from '../../context/AuthContext';
import { TeamMember, User } from '../../types';

interface SeasonResetDialogProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
  teamMembers: TeamMember[];
  onResetComplete: () => void;
}

const SeasonResetDialog: React.FC<SeasonResetDialogProps> = ({
  open,
  onClose,
  teamId,
  teamMembers,
  onResetComplete,
}) => {
  const { user } = useAuth();
  const [resetType, setResetType] = useState<'individual' | 'team'>('individual');
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [seasonName, setSeasonName] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberUsers, setMemberUsers] = useState<User[]>([]);

  useEffect(() => {
    if (open && teamId) {
      loadMemberUsers();
      // Reset form
      setResetType('individual');
      setSelectedMemberId('');
      setSeasonName('');
      setConfirmationText('');
      setError('');
      setSuccess('');
    }
  }, [open, teamId]);

  const loadMemberUsers = async () => {
    if (!user) return;
    
    setLoadingMembers(true);
    setError('');
    
    try {
      // Load user details for each member
      const userPromises = teamMembers.map(member =>
        userService.getUserById(member.athleteId)
      );
      const users = await Promise.all(userPromises);
      setMemberUsers(users.filter(u => u !== null) as User[]);
    } catch (err: any) {
      console.error('Error loading member users:', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!seasonName.trim()) {
      setError('Please enter a season name');
      return;
    }
    
    if (resetType === 'individual' && !selectedMemberId) {
      setError('Please select a team member');
      return;
    }
    
    if (confirmationText !== 'CONFIRM') {
      setError('Please type CONFIRM to confirm the reset');
      return;
    }
    
    if (!user) {
      setError('User not authenticated');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      if (resetType === 'individual') {
        const result = await seasonService.resetUserSeason(
          selectedMemberId,
          teamId,
          seasonName.trim(),
          user.id
        );
        
        if (result.success) {
          setSuccess(result.message);
          setTimeout(() => {
            setSelectedMemberId('');
            setSeasonName('');
            setConfirmationText('');
            onResetComplete();
            onClose();
          }, 1500);
        } else {
          setError(result.message);
        }
      } else {
        // Team reset
        const result = await seasonService.resetTeamSeason(
          teamId,
          seasonName.trim(),
          user.id
        );
        
        if (result.success) {
          setSuccess(result.message);
          setTimeout(() => {
            setSeasonName('');
            setConfirmationText('');
            onResetComplete();
            onClose();
          }, 1500);
        } else {
          setError(result.message);
          // Show detailed results if available
          if (result.results && result.results.length > 0) {
            const failedResults = result.results.filter(r => !r.success);
            if (failedResults.length > 0) {
              setError(`${result.message}. Failed: ${failedResults.map(r => r.message).join(', ')}`);
            }
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reset season');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setResetType('individual');
      setSelectedMemberId('');
      setSeasonName('');
      setConfirmationText('');
      setError('');
      setSuccess('');
      onClose();
    }
  };

  const getMemberDisplayName = (memberId: string): string => {
    const memberUser = memberUsers.find(u => u.id === memberId);
    return memberUser?.displayName || memberId;
  };

  const eligibleMembers = teamMembers.filter(member => {
    // Filter out coaches/head coaches if needed, or include all members
    return true; // For now, allow resetting any member
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Reset Season</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success">
                {success}
              </Alert>
            )}

            <TextField
              label="Season Name"
              value={seasonName}
              onChange={(e) => setSeasonName(e.target.value)}
              placeholder="e.g., 2026 Track Season"
              required
              fullWidth
              disabled={loading}
              helperText="Enter a descriptive name for this season"
            />

            <FormControl component="fieldset" disabled={loading}>
              <FormLabel component="legend">Reset Type</FormLabel>
              <RadioGroup
                value={resetType}
                onChange={(e) => {
                  setResetType(e.target.value as 'individual' | 'team');
                  setSelectedMemberId('');
                }}
              >
                <FormControlLabel
                  value="individual"
                  control={<Radio />}
                  label="Reset Individual Member"
                />
                <FormControlLabel
                  value="team"
                  control={<Radio />}
                  label="Reset Entire Team"
                />
              </RadioGroup>
            </FormControl>

            {resetType === 'individual' && (
              <FormControl fullWidth disabled={loading || loadingMembers}>
                <InputLabel>Select Team Member</InputLabel>
                <Select
                  value={selectedMemberId}
                  onChange={(e) => setSelectedMemberId(e.target.value)}
                  label="Select Team Member"
                  required
                >
                  {eligibleMembers.map((member) => (
                    <MenuItem key={member.id} value={member.athleteId}>
                      {getMemberDisplayName(member.athleteId)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {resetType === 'team' && (
              <Alert severity="warning">
                This will reset points and levels for all {teamMembers.length} team members.
                Each member's progress will be saved as a season snapshot in their profile.
              </Alert>
            )}

            <Alert severity="info">
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>What will be reset:</strong>
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
                <li>Experience Points (XP) → 0</li>
                <li>Wallet Points (WP) → 0</li>
                <li>Level → 1</li>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                <strong>What will be preserved:</strong>
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
                <li>Current streak</li>
                <li>Longest streak</li>
                <li>Last logged dates</li>
                <li>All achievements, badges, and PRs (saved in season snapshot)</li>
              </Typography>
            </Alert>

            <TextField
              label="Type CONFIRM to proceed"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              required
              fullWidth
              disabled={loading}
              error={confirmationText !== '' && confirmationText !== 'CONFIRM'}
              helperText={
                confirmationText !== '' && confirmationText !== 'CONFIRM'
                  ? 'Please type exactly "CONFIRM"'
                  : 'This action cannot be undone'
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || !seasonName.trim() || (resetType === 'individual' && !selectedMemberId) || confirmationText !== 'CONFIRM'}
          >
            {loading ? <CircularProgress size={24} /> : 'Reset Season'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SeasonResetDialog;

